/* Core & libraries */
import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import {
  GET_JOIN_PAGE_STATIC_EN_CONTENT_ENDPOINT,
  GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
  GET_JOIN_PAGE_STATIC_FR_CONTENT_ENDPOINT,
  GET_JOIN_PAGE_STATIC_PT_CONTENT_ENDPOINT,
  GET_SUPPLIER_LIST,
  JOIN_UNITED_DL_PROGRAM_ENDPOINT,
  JOIN_AIRLINE_PROGRAM_ENDPOINT,
  DELINK_PROGRAM_ENDPOINT,
  GET_HOME_PAGE_STATIC_ES_CONTENT_ENDPOINT,
  GET_HOME_PAGE_STATIC_EN_CONTENT_ENDPOINT,
  GET_HOME_PAGE_STATIC_PT_BR_CONTENT_ENDPOINT,
} from '../endpoints/endpoints';
import { AIRLINEPROGRAMS_ROUTE } from "../constants/routes"
import { useUserContext } from "../contexts/userContext";
import { useNotificationContext } from "../contexts/notificationContext";
import { useGlobalContext } from "../contexts/globalContext";
import { useLocaleContext } from "../contexts/localeContext";

/* Components */
/* Styles */

export const useAirlineProgramsService = () => {
  const { genericApiFetch } = useGenericApiFetch();

  const userDetails = useUserContext();
  const globalData = useGlobalContext();
  const locale = useLocaleContext();
  const { setNotification } = useNotificationContext();
  const navigate = useNavigate();

  const [airlineProgramsData, setAirlineProgramsData] = useState({});
  const [staticContent, setStaticContent] = useState(globalData.staticContent || {})

  const userEntityId = userDetails?.userInfo?.userEntityId;

  const getStaticContent = async () => {
    const localeEndpointMapping = {
      en_US: GET_JOIN_PAGE_STATIC_EN_CONTENT_ENDPOINT,
      en_MX: GET_JOIN_PAGE_STATIC_EN_CONTENT_ENDPOINT,
      en_CA: GET_JOIN_PAGE_STATIC_EN_CONTENT_ENDPOINT,
      es_ES: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      es_US: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      es_MX: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      es_CO: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      es_CL: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      es_PE: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      es_AR: GET_JOIN_PAGE_STATIC_ES_CONTENT_ENDPOINT,
      fr_FR: GET_JOIN_PAGE_STATIC_FR_CONTENT_ENDPOINT,
      fr_CA: GET_JOIN_PAGE_STATIC_FR_CONTENT_ENDPOINT,
      pt_PT: GET_JOIN_PAGE_STATIC_PT_CONTENT_ENDPOINT,
      pt_BR: GET_JOIN_PAGE_STATIC_PT_CONTENT_ENDPOINT,
      'PT-BR': GET_JOIN_PAGE_STATIC_PT_CONTENT_ENDPOINT,
    };

    let currentEndpointText = localeEndpointMapping[locale];

    if (currentEndpointText) {
      const response = await genericApiFetch({
        url: currentEndpointText,
        method: "GET",
      });
      if (response.success) {
        setStaticContent(response.data);
        return true;
      }
    }

    return false;
  };

  const getAirlineProgramsData = async (id) => {
    const response = await genericApiFetch({
      url: GET_SUPPLIER_LIST.replace("{userentityid}", id ? id : userEntityId),
      method: "GET",
    });

    if (response.success) {
      setAirlineProgramsData(response.data);
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.getData.errorTitle" />,
        <FormattedMessage id="notifications.getData.errorText" />
      );
    }
  };

  const setAirlineProgramDetails = async (programDetailsData) => {
    const response = await genericApiFetch({
      url: GET_SUPPLIER_LIST.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: programDetailsData,
    });
    if (response.success) {
      return true;
    }
    return false;
  };

  const joinUnitedAirlines = async (joinUnitedData, providerEntityID) => {
    const response = await genericApiFetch({
      url: JOIN_UNITED_DL_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        ...joinUnitedData,
        providerEntityId: providerEntityID
      },
      spinner: "joinUnitedSpinner"
    });

    if (response.success) {
      if (response.data.status === "ERROR") {
        const errorCode = response?.data?.errors[0]?.errorCode;
        if (errorCode === '1000') {
          setNotification(
            "error",
            <FormattedMessage id="notifications.join.UA.errorTitle" />,
            <FormattedMessage id="notifications.join.UA.invalidMileagePlus" />)
        }
        if (errorCode === '1002' || errorCode === '1007') {
          setNotification(
            "error",
            <FormattedMessage id="notifications.join.UA.errorTitle" />,
            <FormattedMessage id="notifications.join.UA.errorText" />)
        }
      } else if (response.data.status === "SUCCESS") {
        navigate('../' + AIRLINEPROGRAMS_ROUTE);
        setNotification(
          "success",
          <FormattedMessage id="notifications.join.UA.successTitle" />,
          <FormattedMessage id="notifications.join.UA.successText" />
        );
        return true;
      }
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.join.UA.errorTitle" />,
        <FormattedMessage id="notifications.join.UA.errorText" />
      )
      return false
    }
  };

  const delinkUnitedAirlines = async (providerEntityID) => {
    const response = await genericApiFetch({
      url: DELINK_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        providerEntityId: providerEntityID
      },
      spinner: "delinkUnitedSpinner"
    })
    if (response.success) {
      navigate('../' + AIRLINEPROGRAMS_ROUTE);
      setNotification(
        "success",
        <FormattedMessage id="notifications.delink.UA.successTitle" />,
        <FormattedMessage id="notifications.delink.UA.successText" />
      );
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )
    return false
  };

  const joinAmericanAirlines = async (providerEntityID) => {
    const response = await genericApiFetch({
      url: JOIN_AIRLINE_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        providerEntityId: providerEntityID
      },
      spinner: "joinAmericanSpinner"
    })
    if (response.success) {
      window.open(response.data.supplierJoinLink, '_self');
      await getAirlineProgramsData()
      return true;
    } else {
      if (response?.errors && response?.errors.length > 0) {
        const errorCode = response?.errors[0]?.errorCode;
        if (errorCode === "1002") {
          setNotification(
            "danger",
            <FormattedMessage id="join.AA.alreadyLinked.title" />,
            <FormattedMessage id="join.AA.alreadyLinked.description" />,
            false,
            30000,
          );
        }

        if (errorCode === "1001") {
          setNotification(
            "danger",
            <FormattedMessage id="join.AA.suplierNotActive.title" />,
            <FormattedMessage id="join.AA.suplierNotActive.description" />,
            false,
            30000,
          );
        }
      } else {
        setNotification(
          "error",
          <FormattedMessage id="notifications.join.AA.errorTitle" />,
          <FormattedMessage id="notifications.join.AA.errorText" />
        )
      }
      return false
    }
  };

  const delinkAmericanAirlines = async (providerEntityID) => {
    const response = await genericApiFetch({
      url: DELINK_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        providerEntityId: providerEntityID
      },
      spinner: "delinkAmericanSpinner"
    })
    if (response.success) {
      navigate('../' + AIRLINEPROGRAMS_ROUTE);
      setNotification(
        "success",
        <FormattedMessage id="notifications.delink.AA.successTitle" />,
        <FormattedMessage id="notifications.delink.AA.successText" />
      );
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )
    return false
  };

  const joinAlaskaAirlines = async (providerEntityID) => {
    const response = await genericApiFetch({
      url: JOIN_AIRLINE_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        providerEntityId: providerEntityID
      },
      spinner: "joinAlaskaSpinner"
    })
    if (response.success) {
      window.open(response.data.supplierJoinLink, '_self');
      await getAirlineProgramsData()
      return true;
    } else {
      if (response?.errors && response?.errors.length > 0) {
        const errorCode = response?.errors[0]?.errorCode;
        if (errorCode === "1002") {
          setNotification(
            "danger",
            <FormattedMessage id="join.AS.alreadyLinked.title" />,
            <FormattedMessage id="join.AS.alreadyLinked.description" />,
            false,
            30000,
          );
        }

        if (errorCode === "1001") {
          setNotification(
            "danger",
            <FormattedMessage id="join.AS.suplierNotActive.title" />,
            <FormattedMessage id="join.AS.suplierNotActive.description" />,
            false,
            30000,
          );
        }
      } else {
        setNotification(
          "error",
          <FormattedMessage id="notifications.join.AS.errorTitle" />,
          <FormattedMessage id="notifications.join.AS.errorText" />
        )
      }
      return false
    }
  };

  const delinkAlaskaAirlines = async (providerEntityID) => {
    const response = await genericApiFetch({
      url: DELINK_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        providerEntityId: providerEntityID
      },
      spinner: "delinkAmericanSpinner"
    })
    if (response.success) {
      navigate('../' + AIRLINEPROGRAMS_ROUTE);
      setNotification(
        "success",
        <FormattedMessage id="notifications.delink.AS.successTitle" />,
        <FormattedMessage id="notifications.delink.AS.successText" />
      );
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )
    return false
  };

  const joinDeltaAirlines = async (joinDeltaData, providerEntityID) => {
    const response = await genericApiFetch({
      url: JOIN_UNITED_DL_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        ...joinDeltaData,
        providerEntityId: providerEntityID      
      },
      spinner: "joinDeltaSpinner"
    })
    if (response.success) {
      if (response.data.status === "ERROR") {
        const errorCode = response?.data?.errors[0]?.errorCode;
        if ( errorCode === '1000' ) {
          setNotification(
            "error",         
            <FormattedMessage id="notifications.join.DL.errorTitle" />,
            <FormattedMessage id="notifications.join.DL.invalidSkyMiles" />)
        }
        return response.data
      } else if (response.data.status === "SUCCESS") {
        navigate('../' + AIRLINEPROGRAMS_ROUTE);
        setNotification(
          "success",
          <FormattedMessage id="notifications.join.DL.successTitle" />,
          <FormattedMessage id="notifications.join.DL.successText" />
        );
        return true;
      }
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.join.DL.errorTitle" />,
        <FormattedMessage id="notifications.join.DL.errorText" />
      )
      return false
    }
  };

  const delinkDeltaAirlines = async (providerEntityID) => {
    const response = await genericApiFetch({
      url: DELINK_PROGRAM_ENDPOINT.replace("{userEntityId}", userEntityId),
      method: "POST",
      data: {
        providerEntityId: providerEntityID
      },
      spinner: "delinkDeltaSpinner"
    })
    if (response.success) {
      navigate('../' + AIRLINEPROGRAMS_ROUTE);
      setNotification(
        "success",
        <FormattedMessage id="notifications.delink.DL.successTitle" />,
        <FormattedMessage id="notifications.delink.DL.successText" />
      );
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )
    return false
  };

  useEffect(() => {
    getStaticContent();
  }, [locale]);

  return {
    getStaticContent,
    getAirlineProgramsData,
    setAirlineProgramDetails,
    joinUnitedAirlines,
    delinkUnitedAirlines,
    joinAlaskaAirlines,
    delinkAlaskaAirlines,
    joinAmericanAirlines,
    delinkAmericanAirlines,
    joinDeltaAirlines,
    delinkDeltaAirlines,
    staticContent,
    airlineProgramsData
  };
};